import React, { useState, useEffect } from 'react'
import { lumi } from '../lib/lumi'

interface ProblemKeyword {
  _id: string
  keyword: string
  description: string
  imageUrl: string
  order: number
  active: boolean
  createdAt: string
  updatedAt: string
}

export function ProblemsKeywordsSection() {
  const [keywords, setKeywords] = useState<ProblemKeyword[]>([])
  const [hoveredKeyword, setHoveredKeyword] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadKeywords()
  }, [])

  const loadKeywords = async () => {
    try {
      const result = await lumi.entities.problem_keywords.list({
        filter: { active: true },
        sort: { order: 1 }
      })
      setKeywords(result.list || [])
    } catch (error) {
      console.error('Erreur chargement mots-clés:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
        </div>
      </section>
    )
  }

  if (keywords.length === 0) {
    return null
  }

  const hoveredData = keywords.find(k => k.keyword === hoveredKeyword)

  return (
    <section className="py-20 px-4 bg-gradient-to-br from-white to-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Les défis des dirigeants
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Vous reconnaissez-vous dans ces situations ? Survolez les mots-clés pour découvrir comment nous pouvons vous aider.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Mots-clés interactifs */}
          <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
            {keywords.map((keyword) => (
              <button
                key={keyword._id}
                onMouseEnter={() => setHoveredKeyword(keyword.keyword)}
                onMouseLeave={() => setHoveredKeyword(null)}
                className={`
                  px-6 py-3 rounded-full font-semibold text-lg transition-all duration-300
                  ${hoveredKeyword === keyword.keyword
                    ? 'bg-gradient-to-r from-teal-600 to-cyan-600 text-white shadow-xl scale-110'
                    : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-teal-500 hover:text-teal-600 hover:shadow-lg'
                  }
                `}
              >
                {keyword.keyword}
              </button>
            ))}
          </div>

          {/* Zone d'affichage de l'image et description */}
          <div className="relative min-h-[400px] bg-white rounded-2xl shadow-xl overflow-hidden">
            {hoveredData ? (
              <div className="h-full animate-fadeIn">
                <img
                  src={hoveredData.imageUrl}
                  alt={hoveredData.keyword}
                  className="w-full h-64 object-cover"
                />
                <div className="p-8">
                  <h3 className="text-3xl font-bold text-teal-600 mb-4">
                    {hoveredData.keyword}
                  </h3>
                  <p className="text-gray-700 text-lg leading-relaxed">
                    {hoveredData.description}
                  </p>
                </div>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center p-8 text-center">
                <div>
                  <div className="w-20 h-20 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <svg className="w-10 h-10 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                    </svg>
                  </div>
                  <p className="text-gray-500 text-lg">
                    Survolez un mot-clé pour découvrir comment nous pouvons vous accompagner
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-in-out;
        }
      `}</style>
    </section>
  )
}
